import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../axiosConfig";
import OwnerLayout from "./OwnerLayout";
import { OwnerContext } from "../../context/OwnerContext";
import Tabs from "../commons/Tabs";
import DogInfo from "./DogInfo";
import OwnerTrainingSummaryList from "./OwnerTrainingSummaryList";
import "../../styles/OwnerMyDogStyle.css";
import OwnerAssignedExercisesList from "./OwnerAssignedExercisesList";
import OwnerAddDogModal from "./OwnerAddDogModal";
import OwnerSurveysList from "./OwnerSurveysList";
import OwnerDogGroups from "./OwnerDogGroups";
import OwnerReportsTab from "./OwnerReportsTab";

const OwnerMyDog = () => {
    const navigate = useNavigate();
    const { owner, loading: ownerLoading, error: ownerError } = useContext(OwnerContext);

    // Multi-dog logic
    const [dogs, setDogs] = useState([]);
    const [selectedDogId, setSelectedDogId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState("");
    const [isAddDogModalOpen, setIsAddDogModalOpen] = useState(false);

    const fetchDogs = async () => {
        if (!owner) return;

        try {
            const token = localStorage.getItem("jwt");
            const response = await axios.get(`/api/v1/dog/${owner.id}/list`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const dogList = response.data || [];
            setDogs(dogList);

            if (dogList.length > 0 && !selectedDogId) {
                setSelectedDogId(dogList[0].id);
            }
        } catch (err) {
            setFetchError("Nie udało się pobrać listy pupili.");
        } finally {
            setLoading(false);
        }
    };

    const handleDogAdded = async () => {
        setLoading(true);
        await fetchDogs();
        const token = localStorage.getItem("jwt");
        const response = await axios.get(`/api/v1/dog/${owner.id}/list`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        const dogList = response.data || [];
        if (dogList.length > 0) {
            setSelectedDogId(dogList[dogList.length - 1].id);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchDogs();
    }, [owner]);

    if (ownerLoading || loading) {
        return (
            <OwnerLayout>
                <p>Ładowanie...</p>
            </OwnerLayout>
        );
    }

    if (ownerError || fetchError) {
        return (
            <OwnerLayout>
                <p className="error">{ownerError || fetchError}</p>
            </OwnerLayout>
        );
    }

    // If no dogs are found, show the "no dog" UI with a button to open the modal.
    if (dogs.length === 0) {
        return (
            <OwnerLayout>
                <div className="no-dog-container">
                    <h1>Nie masz jeszcze dodanego pupila</h1>
                    <button className="add-dog-btn" onClick={() => setIsAddDogModalOpen(true)}>Dodaj pupila</button>
                    {isAddDogModalOpen && (
                        <OwnerAddDogModal
                            isOpen={isAddDogModalOpen}
                            onClose={() => setIsAddDogModalOpen(false)}
                            onDogAdded={handleDogAdded}
                        />
                    )}
                </div>
            </OwnerLayout>
        );
    }

    // Find the currently selected dog by ID
    const selectedDog = dogs.find((d) => d.id === selectedDogId);

    const handleDogTabClick = (dogId) => {
        setSelectedDogId(dogId);
    };

    return (
        <OwnerLayout>
            <div className="dog-details-container">
                <div className="dog-tabs-row">
                    <div className="dog-tabs-list">
                        {dogs.map((d) => (
                            <button
                                key={d.id}
                                onClick={() => handleDogTabClick(d.id)}
                                className={d.id === selectedDogId ? "dog-tab-active" : "dog-tab"}
                            >
                                {d.name}
                            </button>
                        ))}
                    </div>
                    <button
                        className="add-dog-btn"
                        onClick={() => setIsAddDogModalOpen(true)}
                    >
                        Dodaj pupila
                    </button>
                </div>

                {selectedDog ? (
                    <>
                        <Tabs
                            tabs={[
                                {
                                    key: "dog_info",
                                    label: "Dane",
                                    endpoint: `/api/v1/dog/details/${selectedDog.id}`,
                                    paginated: false,
                                    renderContent: (data) => <DogInfo dog={data} />,
                                },
                                {
                                    key: "recommendations",
                                    label: "Zalecenia",
                                    endpoint: `/api/v1/training-summary/dog/${selectedDog.id}`,
                                    paginated: true,
                                    renderContent: (data) => <OwnerTrainingSummaryList summaries={data} />,
                                },
                                {
                                    key: "exercises",
                                    label: "Ćwiczenia",
                                    endpoint: `/api/v1/exercise/dog/${selectedDog.id}/assigned`,
                                    paginated: true,
                                    renderContent: (data) => <OwnerAssignedExercisesList exercises={data} />,
                                },
                                // TODO: naprawić grupy
                                // {
                                //     key: "groups",
                                //     label: "Grupy",
                                //     endpoint: `/api/v1/training-group/${selectedDog.id}/dog`,
                                //     paginated: false,
                                //     renderContent: (data) => <OwnerDogGroups groups={data} dogId={selectedDog.id} />,
                                // },
                                {
                                    key: "surveys",
                                    label: "Ankiety",
                                    endpoint: `/api/v1/survey/dog/${selectedDog.id}/assigned`,
                                    paginated: false,
                                    renderContent: (data) => <OwnerSurveysList surveys={data} dogId={selectedDog.id} />,
                                },
                                {
                                    key: "reports",
                                    label: "Raporty",
                                    endpoint: `/api/v1/reports/dog/${selectedDog.id}/reports`,
                                    paginated: false,
                                    renderContent: (data) => <OwnerReportsTab dogOwnerReports={data} dogId={selectedDog.id} />,
                                }
                            ]}
                            activeId={selectedDog.id}
                        />
                    </>
                ) : (
                    <div className="no-dog-container">
                        <h1>Nie masz jeszcze dodanego pupila</h1>
                        <button className="no-dog-add-button" onClick={() => setIsAddDogModalOpen(true)}>Dodaj pupila +</button>
                    </div>
                )}
            </div>

            {isAddDogModalOpen && (
                <OwnerAddDogModal
                    isOpen={isAddDogModalOpen}
                    onClose={() => setIsAddDogModalOpen(false)}
                    onDogAdded={handleDogAdded}
                />
            )}
        </OwnerLayout>
    );
};

export default OwnerMyDog;